import TechTreeCanvas from "./techTreeCanvas";
import TechNodeInspector from "./techNodeInspector";
import {useDrag, useDrop} from "react-dnd";
import {Metadata, RemoteConstantsModel, TechTreeComponent} from "../../models/remoteConstants.model";
import {useIntegrator} from "../../integrator.context";
import {RemoveTechUnlockCommand} from "../../commands/removeTechUnlock.command";
import {BatchCommand} from "../../commands/batch.command";
import EntityLabel from "../../elements/entityLabel";
import MetadataDragLabel from "../../elements/metadataDragLabel";
import {useApp} from "../../app.context";
import {getDuplicateIds} from "../../utils/metadata.utils";
import {MetadataCollection} from "../../commands/metadataValue.command";
import {ChangeMetadataCommand} from "../../commands/changeMetadata.command";
import PriceList from "./prices";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import {useNavTarget} from "../../elements/useNavTarget";
import {NavTarget} from "../../utils/navTarget";
import UnitSources from "../../elements/unitSources.element";
import {ReactNode, useRef, useState} from "react";
import {findTechCycle} from "../../utils/techTree.utils";

type AssetTab = 'Units' | 'Buildings' | 'Actions' | 'Passives';

function TechTreeEditorView() {
    // Resizable palette: a vertical bar drags the column width, a horizontal
    // bar splits the Prices (top) and Assets (bottom) sections. See
    // elements/resizeBar; no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const paletteRef = useRef<HTMLDivElement>(null);
    const inspectorRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.techTreePaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the tree canvas at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });
    const prices = useResizable({
        storageKey: 'rift.editor.techTreePricesHeight',
        axis: 'y', min: 80, initial: 240,                       // Prices sits above the bar
        // Reserve the bar + gaps + the Assets minimum at the bottom, and the
        // inspector above: it's always on screen and its height varies with the
        // selected node's unlock count, so it's measured rather than assumed.
        max: () => (paletteRef.current?.getBoundingClientRect().height ?? Infinity)
            - (inspectorRef.current?.getBoundingClientRect().height ?? 0) - 140,
    });

    // Selection is owned here so the canvas and the inspector agree on it.
    const [selected, setSelected] = useState<string | undefined>(undefined);

    // A loop can't be drawn here, but can arrive with the data (Compare Branches,
    // older exports); it blocks export, so say so where it can be fixed.
    const {state} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const loop = findTechCycle(state.techTree ?? []);

    return (
        // minHeight: 0 pins the tab to the viewport row (as the Maps tab does):
        // without it the palette's fixed-height children set a content-based
        // floor, the whole tab scrolls, and the canvas stretches past the window.
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1, minHeight: 0}}>
            <div className={'layout center flex-grow'} style={{position: 'relative', overflow: 'hidden', borderRadius: 'var(--border-radius)', border: 'solid 1px var(--overlay-color)', minWidth: 0}}>
                <TechTreeCanvas selected={selected} onSelect={setSelected}/>
                {loop &&
                    <div style={{position: 'absolute', top: 10, left: 10, right: 10, padding: '8px 12px', borderRadius: 'var(--border-radius)',
                                 background: 'var(--box-color)', border: 'solid 1px var(--error-color)', pointerEvents: 'none'}}>
                        <strong style={{color: 'var(--error-color)'}}>The tech tree has a loop</strong>
                        {`: ${loop.map(id => getLocalizationValue(id)).join(' → ')}. Shift+drag between two of these nodes to remove a link. Export is blocked until it's gone.`}
                    </div>}
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            {/* Bounded and self-scrolling: the inspector is auto-height (it grows
                with the selected node's unlock count) and Prices is a fixed drag
                height, so a short window can leave the two of them taller than
                the column. Prices yields down to its drag minimum first; only if
                that isn't enough does the column scroll. */}
            <div ref={paletteRef} className={'layout vertical'}
                 style={{width: palette.size, flexShrink: 0, minHeight: 0, overflowY: 'auto'}}>
                {/* Inspector for the selected node; auto-height, above Prices.
                    Always present — with nothing selected it shows Root, which
                    is the only place root is editable now the canvas omits it. */}
                <div ref={inspectorRef} style={{flexShrink: 0, paddingBottom: 4}}>
                    <TechNodeInspector id={selected} onSelect={setSelected}/>
                </div>

                {/* flexShrink lets Prices give space back when the column is
                    short, down to the same minimum the drag enforces — so the
                    Assets list below stays usable instead of being squeezed out. */}
                <div style={{height: prices.size, flexShrink: 1, minHeight: 80, overflowY: 'auto'}}>
                    <PriceList/>
                </div>

                {/* Draggable separator between the Prices and Assets sections. */}
                <ResizeBar axis={'y'} {...prices.handleProps}/>

                <AssetsList/>
            </div>
        </div>
    );
}

function AssetsList() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const actionDupes = getDuplicateIds(state.techActionAttributes ?? []);
    const passiveDupes = getDuplicateIds(state.techPassiveAttributes ?? []);
    const [tab, setTab] = useState<AssetTab>('Units');

    // The assignment half of a palette row: the node-name suffix a designer
    // reads, and the button that frees the entity again. Both come out of one
    // lookup, so a row can never name one node and unassign from another.
    // Empty when nothing unlocks the entity — an unassigned row gets neither.
    function assignment(unlock: string): {suffix?: string, unassignTitle?: string, onUnassign?(): void} {
        const nodes = assigningNodes(state, unlock);
        if (nodes.length === 0) return {};

        const names = nodes.map(n => getLocalizationValue(n.id)).join(', ');
        // One command per occurrence: RemoveTechUnlockCommand drops a single
        // entry, and a ruleset edited outside the editor can list the same
        // unlock more than once. Batched so the whole thing is one undo.
        const removals = nodes.flatMap(n =>
            (n.unlocks ?? []).filter(u => u === unlock).map(() => new RemoveTechUnlockCommand(n.id, unlock)));
        return {
            suffix: ` (${names})`,
            unassignTitle: `Unassign from ${names}`,
            onUnassign: () => executeCommand(removals.length === 1 ? removals[0] : new BatchCommand(removals)),
        };
    }

    // The "show while locked" eye toggle of an action row. Only on an action a node
    // unlocks: with nothing to research, an unassigned action always shows anyway.
    function showWhileLocked(metadata: Metadata): {showWhileLocked?: boolean, onShowWhileLockedChange?(value: boolean): void} {
        if (!alreadyApplied(state, metadata.uid)) return {};

        return {
            showWhileLocked: metadata.showWhileLocked ?? false,
            onShowWhileLockedChange: (value: boolean) => executeCommand(new ChangeMetadataCommand(
                metadata.uid, MetadataCollection.TechActions, m => {
                    // Only ever written as true, so absent keeps meaning "hidden until unlocked".
                    if (value) m.showWhileLocked = true;
                    else delete m.showWhileLocked;
                })),
        };
    }

    const [{canDrop}, drop] = useDrop({
        accept: 'entity',
        drop: (item: any) => executeCommand(new RemoveTechUnlockCommand(item.nodeId, item.id)),
        collect: (monitor) => {
            return {
                canDrop: monitor.isOver({shallow: true})
            };
        },
    });

    return (
        <div ref={drop} className={'layout vertical gap box'} style={{
            height: 0,
            flexGrow: 1,
            overflowY: 'auto',
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <div className={'layout horizontal gap'} style={{width: '100%'}}>
                <AssetTabButton label={'Units'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Buildings'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Actions'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Passives'} current={tab} onClick={setTab}/>
            </div>
            <div className={'layout vertical gap'} style={{
                flexGrow: 1,
                overflowY: 'auto'
            }}>
                {tab === 'Units' && [...state.units]
                    .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                    .map((unit, i) => <EntityDragLabel key={i} id={unit.id} navTarget={{kind: 'unit', id: unit.id}} {...assignment(unit.id)}
                                                       detail={<UnitSources unitId={unit.id}/>}/>)}
                {tab === 'Buildings' && [...state.buildings]
                    .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                    .map((building, i) => <EntityDragLabel key={i} id={building.id} navTarget={{kind: 'building', id: building.id}} {...assignment(building.id)}/>)}
                {tab === 'Actions' && state.techActionAttributes && [...state.techActionAttributes]
                    .sort((a, b) => getLocalizationValue(a.uid).localeCompare(getLocalizationValue(b.uid)))
                    .map((metadata, index) => <MetadataDragLabel key={index} uid={metadata.uid} id={metadata.id} type={'entity'} values={metadata.values} entities={metadata.entities} collection={MetadataCollection.TechActions} duplicate={actionDupes.has(metadata.id)} draggable={() => !alreadyApplied(state, metadata.uid)} {...assignment(metadata.uid)} {...showWhileLocked(metadata)}/>)}
                {tab === 'Passives' && state.techPassiveAttributes && [...state.techPassiveAttributes]
                    .sort((a, b) => getLocalizationValue(a.uid).localeCompare(getLocalizationValue(b.uid)))
                    .map((metadata, index) => <MetadataDragLabel key={index} uid={metadata.uid} id={metadata.id} type={'entity'} values={metadata.values} entities={metadata.entities} collection={MetadataCollection.TechPassives} duplicate={passiveDupes.has(metadata.id)} draggable={() => !alreadyApplied(state, metadata.uid)} {...assignment(metadata.uid)}/>)}
            </div>
        </div>
    )
}

function AssetTabButton(props: {label: AssetTab, current: AssetTab, onClick: (t: AssetTab) => void}) {
    const selected = props.label === props.current;
    return (
        <button
            className={`layout horizontal center stretch bar-state ${selected ? 'bar-state-active' : 'bar-state-inactive'}`}
            style={{flexGrow: 1}}
            onClick={() => props.onClick(props.label)}>
            {props.label}
        </button>
    );
}

function EntityDragLabel(props: {id: string, suffix?: string, navTarget?: NavTarget, unassignTitle?: string, onUnassign?(): void, detail?: ReactNode}) {
    const {state} = useIntegrator();
    const {setNotification} = useApp();

    const [{isDragging}, drag] = useDrag({
        type: 'entity',
        item: {
            id: props.id,
        },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        canDrag: !alreadyApplied(state, props.id)
    });

    // An assigned row is dimmed so the unassigned ones stand out, but it is also
    // the row carrying the Unassign button — so the dim lands on the row's
    // content rather than on the row, leaving the button at full strength (see
    // .dim-row-content).
    const dimmed = isDragging || alreadyApplied(state, props.id);

    // Pass the building's startLevel for building unlocks so the image
    // resolves through the pack (matches the Tiles tab palette). For non-
    // buildings (units / tech attributes), level defaults to 0.
    const buildingData = state.buildings.find(b => b.id === props.id);

    return (
        <div ref={drag} {...useNavTarget(props.navTarget)}
             className={dimmed ? 'dim-row-content' : undefined} onClick={() => {
            setNotification({
                title: props.id,
                message: props.id + '.description',
                edit: true
            })
        }}>
            <EntityLabel id={props.id} level={buildingData?.startLevel} suffix={props.suffix}
                         unassignTitle={props.unassignTitle} onUnassign={props.onUnassign} detail={props.detail}/>
        </div>
    );
}

function alreadyApplied(remoteConstants: RemoteConstantsModel, unlock: string): boolean {
    return assigningNodes(remoteConstants, unlock).length > 0;
}

// Every tech node whose `unlocks` array contains the given id/uid. Normally one
// at most — the palette refuses to drag an entity that is already assigned — but
// a ruleset edited outside the editor can carry the same unlock on two nodes,
// and unassigning has to clear all of them or the row stays greyed out after the
// click. Feeds the right-hand list, so designers see at a glance which node
// carries each entity.
function assigningNodes(remoteConstants: RemoteConstantsModel, unlock: string): TechTreeComponent[] {
    return remoteConstants.techTree.filter(t =>
        t.unlocks !== undefined && t.unlocks !== null && t.unlocks.some(a => a === unlock));
}

export default TechTreeEditorView;